// Pure recomputation of the Estimate tab's totals from an order document, so other
// tabs (e.g. Accounting) can show / sync the estimate total without the user having
// to open the Estimate tab. This MUST stay in sync with the math in
// src/pages/ServiceOrderPage/Estimate.js (calculateTotals / flat-invoice block).

const SKYWAY_ID = '9c0d88f5-84f9-454d-833d-a8ced9adad49';

const isValidNumber = (value) => !(value === null || value === undefined || value === '');

// Returns null when there's nothing to base an estimate on (no selected services,
// parts, or subcontracts). Otherwise returns a breakdown including:
//   - total:       full customer-facing total INCLUDING sales tax (+ subcontract)
//   - preTaxTotal: same, excluding sales tax
//   - serviceTotal / partsTotal / subcontractTotal / subtotal / shopSuppliesFee /
//     wasteFee / discount / tax / taxRate / documentType
export function computeEstimateTotals(order) {
  if (!order) return null;

  const customerId = order.customer && (typeof order.customer === 'string' ? order.customer : order.customer.id);
  const isSkyway = customerId === SKYWAY_ID;

  const DEFAULT_MARGIN = isSkyway ? 0 : 35;
  const TAX_RATE = isSkyway ? 0 : 0.06;
  const laborRate = isSkyway ? 100 : (order.laborRate || 150);
  const shopSuppliesRate = isSkyway ? 0 : (isValidNumber(order.shopSuppliesRate) ? order.shopSuppliesRate : 0.03);
  const wasteFee = isSkyway ? 0 : (isValidNumber(order.wasteFee) ? order.wasteFee : 50);
  const discount = order.discount || 0;
  const taxEnabled = order.taxEnabled === false ? false : true;
  const documentType = order.documentType || 'estimate';
  const isFlat = documentType === 'flat-invoice';

  const services = order.services || [];
  const parts = order.parts || [];
  const subcontracts = order.subcontracts || [];

  const selServiceIds = order.selectedServiceIds || services.map(s => s.id);
  const selPartIds = order.selectedPartIds || parts.map(p => p.id);
  const selSubIds = order.selectedSubcontractIds || subcontracts.map(s => s.id);

  // Declined items never count toward the estimate, even if still selected.
  const selectedServices = services.filter(s => selServiceIds.includes(s.id) && !s.declined);
  const selectedParts = parts.filter(p => selPartIds.includes(p.id) && !p.declined);
  const selectedSubcontracts = subcontracts.filter(s => selSubIds.includes(s.id) && !s.declined);

  if (!selectedServices.length && !selectedParts.length && !selectedSubcontracts.length) {
    return null;
  }

  const computedServiceTotal = selectedServices.reduce((sum, s) => sum + (s.time || 1) * laborRate, 0);
  const computedPartsTotal = selectedParts.reduce((sum, p) => {
    const margin = isValidNumber(p.margin) ? p.margin : DEFAULT_MARGIN;
    const qty = p.quantity || 1;
    return sum + (p.cost || 0) * (1 + margin / 100) * qty;
  }, 0);
  const subcontractTotal = selectedSubcontracts.reduce((sum, s) => {
    const margin = isValidNumber(s.margin) ? s.margin : DEFAULT_MARGIN;
    return sum + (s.cost || 0) * (1 + margin / 100);
  }, 0);

  // Flat invoices can override the service/parts totals with manual lump sums.
  const serviceTotal = isFlat && order.flatServiceTotal != null ? order.flatServiceTotal : computedServiceTotal;
  const partsTotal = isFlat && order.flatPartsTotal != null ? order.flatPartsTotal : computedPartsTotal;
  const subtotal = serviceTotal + partsTotal;

  // Shop supplies fee is always based on the computed (non-flat) subtotal, matching
  // Estimate.js's calculateTotals which recomputes it from the live selection.
  const shopSuppliesFee = (computedServiceTotal + computedPartsTotal) * shopSuppliesRate;

  const adjustedSubtotal = subtotal + shopSuppliesFee + wasteFee - discount;
  // Tax only applies when there are parts on the document.
  const tax = (taxEnabled && selectedParts.length > 0) ? adjustedSubtotal * TAX_RATE : 0;

  // Subcontracted work is non-taxable and added after tax.
  const preTaxTotal = adjustedSubtotal + subcontractTotal;
  const total = adjustedSubtotal + tax + subcontractTotal;

  const round2 = (n) => Math.round(n * 100) / 100;

  return {
    documentType,
    serviceTotal: round2(serviceTotal),
    partsTotal: round2(partsTotal),
    subcontractTotal: round2(subcontractTotal),
    subtotal: round2(subtotal),
    shopSuppliesFee: round2(shopSuppliesFee),
    wasteFee: round2(wasteFee),
    discount: round2(discount),
    tax: round2(tax),
    taxRate: TAX_RATE,
    preTaxTotal: round2(preTaxTotal),
    total: round2(total),
  };
}
